/**
 * ATL-1445 stage 3, P9b — THE CUT-OVER PREVIEW: a staging-only build constant (design §6, §18).
 *
 * Stage 3 converts the W writers one domain at a time (P9–P13), and a bundle is write-cutover CAPABLE
 * only when every one of them is converted (`writeWCapability.ts`). Until then no session anywhere
 * can be in Supabase mode, so nothing about outbox v2, the local view, the watcher's reload or the
 * waiters can be exercised end to end. §6's answer: on STAGING only, a build may enter Supabase mode
 * with an incomplete manifest — and in that mode every UNCONVERTED W writer refuses visibly
 * («Недоступно в предпросмотре перехода», `assertWWriterAvailable` in `writeW.ts`) instead of writing
 * Firestore-first. One master, so the sales and cashbox paths are genuinely tested; nothing is proved
 * about stock or money parity across writers (that needs the full domain: the stage-3 exit run).
 *
 * WHICH BUILD IS A PREVIEW BUILD — a POSITIVE allowlist, fail-closed (P9b review 1). ALL THREE of the
 * build's compiled-in values must name staging; a value that is missing, empty, or anything else
 * (production's, a local developer's, a typo) makes the build NOT a preview:
 *  - `EXPO_PUBLIC_FIREBASE_PROJECT_ID === 'qarasay-staging'`;
 *  - `EXPO_PUBLIC_SUPABASE_URL === 'https://syfnmohksikmfrkfiksv.supabase.co'` (the staging project's
 *    URL exactly: `.env.staging` `EXPO_PUBLIC_SUPABASE_URL` and `STAGING_SUPABASE_PROJECT_REF`, and
 *    `eas.json` `staging`);
 *  - `EXPO_PUBLIC_BACKEND_API_URL` is the staging Cloud Run service's `/api`:
 *    `https://[<tag>---]atlas-backend-uufb7nerhq-ey.a.run.app/api` — the default stack's untagged URL
 *    (`eas.json` `staging`) or a slug stack's tagged revision URL (`scripts/staging/deploy.sh`
 *    `resolve_backend_url`, the legacy Cloud Run form `scripts/staging/lib/bundle-api-base.mjs`
 *    documents as what staging serves). `uufb7nerhq` is the staging project's Cloud Run hash: production's
 *    `atlas-backend` answers at a different `*.run.app` host, and at `api.atlas-app.org`. If staging's
 *    service ever moves to Cloud Run's newer `<service>-<projectnumber>.<region>.run.app` form, the preview
 *    turns OFF (fail-closed) until this pattern is extended.
 * Production's configuration fails all three (`eas.json` `production` / `productionAPK`; the web and OTA
 * builds' values come from `deploy.yml` secrets). So does a break-glass `npm run update` from a local
 * `.env` that sets the staging project id but no staging URLs: a denylist would have let that through.
 * `writeWPreview.prodBuild.test.js` pins every case, and transforms this file the way production Metro
 * does to show the constant compiles to the literal `false` for production's values.
 *
 * WHAT THE PROJECT ID DOES AND DOES NOT DECIDE. On WEB it decides which Firestore the bundle talks to
 * (`global/Firebase/firebaseConfig.js` builds the web SDK's config from these `EXPO_PUBLIC_FIREBASE_*`
 * values). On NATIVE it does NOT: `@react-native-firebase` reads `GoogleService-Info.plist` /
 * `google-services.json`, which `app.config.js` selects at BUILD time from `MY_ENVIRONMENT` (`staging`
 * → the `.staging` files and the `kz.qarasay.kassa.staging` app id). `MY_ENVIRONMENT` is not an
 * `EXPO_PUBLIC_*` variable, so nothing of it is inlined into the bundle: there is NO compile-time
 * bundle-visible signal of which Firebase project a native binary talks to (the native app id and the
 * native Firebase app's options exist only at RUNTIME, and this constant is deliberately decided at build
 * time — nothing read at runtime can turn a bundle into a preview). What holds natively instead:
 *  - the only EAS profile whose values pass the allowlist is `staging`, and it sets
 *    `MY_ENVIRONMENT=staging` (pinned by the test), so every preview binary EAS builds talks to the
 *    staging Firebase project;
 *  - a misbuilt native binary or OTA (staging `EXPO_PUBLIC_*` values on a production native build —
 *    production's GoogleService files) compiles `WRITE_W_PREVIEW_BUILD` to `true` while talking to
 *    PRODUCTION's Firestore. It is not inert: an unpinned preview refuses every guarded W write for
 *    EVERY organization until its write mode is pinned, and for the whole session if the pin never lands
 *    (`currentSessionWriteMode`, ATL-1678); pinned to a Supabase-master organization it would go
 *    backend-first to STAGING's backend. So the preview is also confirmed at RUNTIME, by a check that can
 *    only DISABLE it (P9b review 2, S3-2): `confirmWriteWPreviewAtRuntime` honours the compiled-in `true`
 *    only when the running Firebase app's `options.projectId` is `qarasay-staging`
 *    (`WRITE_W_CAPABILITY`, `writeWCapability.ts`). An unreadable value is a refusal (fail-closed). The
 *    check runs only behind the build constant (`WRITE_W_PREVIEW_BUILD && …`), so a non-preview build
 *    never loads the Firebase module for it, never reads anything, and adds no microtask (it is
 *    synchronous). On web it is redundant — `firebaseConfig.js` builds the web app's `projectId` from the
 *    same inlined value — and harmless. It keeps the rule above: a runtime value can turn a preview OFF,
 *    never ON. (Such a build is broken far beyond the preview: every API call goes to staging.)
 *
 * WHY THE READS ARE LITERAL `process.env.EXPO_PUBLIC_*` MEMBERS. `babel-preset-expo` inlines a static
 * `process.env.EXPO_PUBLIC_*` member read at build time (`inline-env-vars.js`), and only that form (a
 * computed key is left alone — `firebaseConfig.inlining.test.js`). So the shipped expression compares
 * literals: nothing is read at runtime to decide it, and no runtime state can turn a production bundle
 * into a preview (the S3-2 runtime check above only ever narrows a `true`).
 *
 * WHAT A PREVIEW BUILD CHANGES:
 *  - for a Supabase-master organization (production has none; staging has only those
 *    `scripts/staging/flip-write-master.mjs` flipped), `decideSessionWriteMode` treats it as able to go
 *    backend-first (reason `org-supabase-master-preview` instead of `bundle-incapable`'s refusal), and
 *    its unconverted W writers refuse in that mode (`assertWWriterAvailable`);
 *  - before its write mode is pinned it REFUSES W writes, as a capable bundle does (`currentSessionWriteMode`,
 *    ATL-1678): it cannot know yet whether Firestore-first would be a second master.
 * Once pinned to a Firestore-master organization a preview build decides exactly what any incapable
 * bundle decides (pinned by `writeMode.test.js`), so ordinary staging QA is unaffected after startup.
 *
 * WHAT IT DOES NOT CHANGE. The bundle's capability stays `capable: false` (`writers-unconverted`): a
 * preview is not capability, the `write-bundle-coverage` gate clause never counts it, and the
 * `writes_master` setter's gates are untouched. It does NOT complete ATL-1601 (the production
 * Firestore-write interceptor, gate `incapable-bundle-w-refusal`): the guard is per registered writer,
 * it runs only in a preview build, and a write outside the registry is not refused by it.
 *
 * Dependency-free on purpose (it is read by `writeWCapability.ts`, which `writeW.ts` imports).
 */

/** The staging Firebase project (`.env.staging` `STAGING_FIREBASE_PROJECT`, `eas.json` `staging`). */
export const WRITE_W_PREVIEW_FIREBASE_PROJECT = 'qarasay-staging'

/** The staging Supabase project's URL, exactly (`.env.staging`, `eas.json` `staging`). */
export const WRITE_W_PREVIEW_SUPABASE_URL = 'https://syfnmohksikmfrkfiksv.supabase.co'

/**
 * Whether a Firebase app handle names the staging project: `app.options.projectId === 'qarasay-staging'`.
 * Both SDKs expose it — `@react-native-firebase/app`'s `firebase.app().options` (read from the binary's
 * GoogleService files, the only native signal) and the web compat app's `options` (`firebaseConfig.js`).
 * Anything that cannot be read as that exact string — no app, no options, a non-string, a getter that
 * throws — is `false`. Pure, so every case is tested (`writeWPreview.runtime.test.js`).
 */
export const isStagingFirebaseApp = (app: unknown): boolean => {
  try {
    if (typeof app !== 'object' || app === null || !('options' in app)) return false
    const { options } = app
    if (typeof options !== 'object' || options === null || !('projectId' in options)) return false
    return options.projectId === WRITE_W_PREVIEW_FIREBASE_PROJECT
  } catch {
    return false
  }
}

/**
 * S3-2: the runtime confirmation of a compiled-in preview (module docblock). It can only DISABLE: the
 * shipped expression is `WRITE_W_PREVIEW_BUILD && confirmWriteWPreviewAtRuntime(…)`, so it is evaluated
 * only in a build that is already a preview, and `false` here turns that build's preview off. A loader
 * that throws is `false`. Synchronous: no microtask. The app is passed in (a loader) to keep this file
 * dependency-free.
 */
export const confirmWriteWPreviewAtRuntime = (loadFirebaseApp: () => unknown): boolean => {
  try {
    return isStagingFirebaseApp(loadFirebaseApp())
  } catch {
    return false
  }
}

/** The three compiled-in values the decision reads (undefined when the build did not set one). */
export interface WriteWPreviewBuildConfig {
  firebaseProjectId: string | undefined
  backendApiUrl: string | undefined
  supabaseUrl: string | undefined
}

/**
 * Whether a build with this configuration is a cut-over preview: every one of the three values names
 * staging, positively (module docblock). Missing or unknown ⇒ false. Pure, so the decision is tested on
 * every configuration a build can carry (`writeWPreview.prodBuild.test.js`). The backend pattern is a
 * regex LITERAL tested against the value — a boolean that passes nothing of the URL on (the census's
 * backend-URL follower holds that, `scripts/wWriterCensus.ts`).
 */
export const isWriteWPreviewBuild = (config: WriteWPreviewBuildConfig): boolean =>
  config.firebaseProjectId === WRITE_W_PREVIEW_FIREBASE_PROJECT &&
  config.supabaseUrl === WRITE_W_PREVIEW_SUPABASE_URL &&
  typeof config.backendApiUrl === 'string' &&
  /^https:\/\/(?:[a-z0-9-]+---)?atlas-backend-uufb7nerhq-ey\.a\.run\.app\/api$/.test(config.backendApiUrl)

/**
 * Whether THIS bundle is a cut-over preview build. Compiled in (see the module docblock): `false` in
 * every production build, `true` only in a build configured entirely against staging. Each read is a
 * static `process.env.EXPO_PUBLIC_*` member, so babel-preset-expo replaces it with the build's literal.
 */
export const WRITE_W_PREVIEW_BUILD: boolean = isWriteWPreviewBuild({
  firebaseProjectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
  backendApiUrl: process.env.EXPO_PUBLIC_BACKEND_API_URL,
  supabaseUrl: process.env.EXPO_PUBLIC_SUPABASE_URL,
})
